/** @jsxImportSource @emotion/react */
import * as React from 'react';
import { connect } from 'react-redux';
import { Column } from '../../layout/flexbox';
import { IAppState } from '../../core/redux/IAppState';
import { getError, getLoading } from '../../core/redux/api/module';
import { ACTION_TYPE } from '../../core/redux/actionType';
import { ApiError } from '../../core/redux/api/apiError';
import { loadReleaseNotesAction } from './loadReleaseNotesAction';
import { LargeText, PAGE_COLUMN, PAGE_CONTENT, PAGE_TITLE } from '../../styles/styles';
import { Loader } from '../../forms/elements/loader/Loader';
import { css } from '@emotion/react';
import ErrorView from '../../forms/ErrorView';
import { isNilOrEmpty } from '../../language/module';
import { IKON_BLUE } from '../../styles/colors';
import { clearReleaseNotesAction } from './clearReleaseNotesAction';
import { getResortDetailsAction } from '../../resortDetails/getResortDetailsAction';
import { AppDocItem } from '../appDocItem';
import AppDocItemView from '../AppDocItemView';
import { getResource } from '../../language/resources/getResource';


const noReleaseNotesError = css`
  display: block;
  margin: 25px auto;
  text-align: center;
  color: ${IKON_BLUE};
  ${LargeText};
`;

const releaseNotesLoadingMsg = css`
  display: block; 
  ${LargeText};
`;

const releaseNotesLoader = css`
  width: 100px;
  height: 100px;
  margin-bottom: 20px;
`;

const releaseNotesLoaderAlignment = css`
  display: flex; 
  flex-direction: column; 
  align-items: center; 
  padding: 25px 0;
`;


type Props = {
  resortAbbrev?: string;
  isLoadingResorts?: boolean;
  releaseNotes?: AppDocItem[];
  isLoading?: boolean;
  error?: ApiError;
  resources: Record<string, any>;
  loadReleaseNotes?: () => void;
  clearReleaseNotes?: () => void;
  fetchResorts?: () => void;
};


/**
 * ReleaseNotesPage: a component that initiates a fetch for the Ikon Release Notes, then renders them or their fetch
 * result. Each Release is expandable and happily renders rich text.
 *
 * PureComponent: true
 * Connected to Redux: true
 */
export default connect(
  (state: IAppState) => {
    const resortAbbrev = state.selectedResort.resortAbbreviation || '';
    const resources    = state.locale;

    return {
      resortAbbrev: resortAbbrev,
      releaseNotes: state.releaseNotes,
      isLoading:    !!getLoading(state, ACTION_TYPE.LOAD_RELEASE_NOTES_REQUEST, ACTION_TYPE.LOAD_RELEASE_NOTES_REQUEST),
      error:        getError(state, ACTION_TYPE.LOAD_RELEASE_NOTES_REQUEST, ACTION_TYPE.LOAD_RELEASE_NOTES_REQUEST),
      resources:    resources,
    };
  },
  (dispatch) => ({
    loadReleaseNotes:  () => dispatch(loadReleaseNotesAction()),
    clearReleaseNotes: () => dispatch(clearReleaseNotesAction()),
    fetchResorts:      () => dispatch(getResortDetailsAction()),
  }),
)(class ReleaseNotesPage extends React.PureComponent<Props> {
  public componentDidMount(): void {
    this.props.loadReleaseNotes!();

    if (isNilOrEmpty(this.props.resortAbbrev)) {
      this.props.fetchResorts!();
    }
  }


  public componentWillUnmount(): void {
    this.props.clearReleaseNotes!();
  }


  public render = () => (
    <Column rootCss={PAGE_COLUMN}>
      <span css={PAGE_TITLE}>{getResource(this.props.resources, 'RELEASE_NOTES_PAGE_TITLE')}</span>
      <div css={PAGE_CONTENT}>
        { // Loading
          this.props.isLoading
          && <div css={releaseNotesLoaderAlignment}>
            <Loader rootCss={releaseNotesLoader}/>
            <span css={releaseNotesLoadingMsg}>{getResource(this.props.resources, 'RELEASE_NOTES_PAGE_LOADING')}</span>
          </div>
        }
        {/* Error */}
        <ErrorView error={this.props.isLoading
                          ? undefined
                          : this.props.error && getResource(this.props.resources, 'RELEASE_NOTES_PAGE_FETCH_FAILED') }/>
        { // Empty
          isNilOrEmpty(this.props.releaseNotes)
          && !this.props.isLoading
          && !this.props.error
          && <span css={noReleaseNotesError}>
            {getResource(this.props.resources, 'RELEASE_NOTES_PAGE_NO_NOTES')}
          </span>
        }
        { // Release Notes (Data)
          !isNilOrEmpty(this.props.releaseNotes) && !this.props.error && this.props.releaseNotes!.map(
            note => <AppDocItemView key={note.title} docItem={note} startCollapsed={true}/>,
          )
        }
      </div>
    </Column>
  );
});
